import { createContext, useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { queryClientAtom } from 'jotai-tanstack-query';
import axiosApiInstance from '../../../api/axiosClient';
import type {
    Campaign, CampaignCreatorDeliverable, CampaignDeliverable, CampaignPost, CampaignReport, CampaignRoster,
    ContentTypeBreakdown, DeliverablePlatform, DeliverablePostType,
    HeatmapResult, LeaderboardInfluencer, LeaderboardResult,
    CampaignTask, PaymentsResult, PerformanceResult, SummaryResult,
} from './types';

const KEY = 'campaigns';

/** Set on the public share page so report queries skip login. */
export const ReportTokenContext = createContext<string | undefined>(undefined);

export const useCampaignQuery = (id: number | string | undefined) =>
    useQuery<Campaign>({
        queryKey: [KEY, 'detail', id],
        enabled: id !== undefined && id !== null && String(id) !== 'new',
        queryFn: async () =>
            (await axiosApiInstance.get<Campaign>(`/api/campaigns/items/${id}`)).data,
        cacheTime: 5 * 60 * 1000,
        staleTime: 30_000,
        refetchInterval: (data) => (data?.refresh_status === 'running' ? 2000 : false),
        refetchIntervalInBackground: true,
    });

export const useCampaignRoster = (id: number | string | undefined) =>
    useQuery<CampaignRoster>({
        queryKey: [KEY, 'roster', id],
        enabled: id !== undefined && id !== null && String(id) !== 'new',
        queryFn: async () =>
            (await axiosApiInstance.get<CampaignRoster>(`/api/campaigns/items/${id}/roster`)).data,
        cacheTime: 5 * 60 * 1000,
        staleTime: 30_000,
    });

export const useCampaignAvatars = (id: number | string | undefined) =>
    useQuery<{ creator_id: number; image: string }[]>({
        queryKey: [KEY, 'avatars', id],
        enabled: id !== undefined && id !== null && String(id) !== 'new',
        queryFn: async () =>
            (await axiosApiInstance.get<{ creator_id: number; image: string }[]>(`/api/campaigns/items/${id}/avatars`)).data,
        cacheTime: 10 * 60 * 1000,
        staleTime: 5 * 60 * 1000,
    });

export const useCampaignAvatarMap = (id: number | string | undefined) => {
    const token = useContext(ReportTokenContext);
    const avatars = useCampaignAvatars(token ? undefined : id);
    const shared = useQuery<{ creator_id: number; image: string }[]>({
        queryKey: ['public-report', token, 'avatars'],
        enabled: !!token,
        queryFn: async () =>
            (await axiosApiInstance.get<{ creator_id: number; image: string }[]>(`/api/public/campaign-reports/${token}/avatars`)).data,
        staleTime: 5 * 60 * 1000,
    });
    const rows = token ? shared.data : avatars.data;
    return useMemo(() => {
        const map: Record<number, string> = {};
        for (const avatar of rows ?? []) map[avatar.creator_id] = avatar.image;
        return map;
    }, [rows]);
};

export const useCreateCampaign = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async (campaign: Partial<Campaign>) =>
            (await axiosApiInstance.post<Campaign>('/api/campaigns/items', campaign)).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [KEY] });
        }
    });
};

export const useUpdateCampaignKeywords = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, tags }: { id: number; tags: string[] }) =>
            (await axiosApiInstance.put<{ hashtags: { id: number; tag: string }[] }>(
                `/api/campaigns/items/${id}/hashtags`,
                { tags },
            )).data,
        onSuccess: (data, vars) => {
            qc.setQueryData<Campaign>([KEY, 'detail', vars.id], (prev) =>
                prev ? { ...prev, hashtags: data.hashtags } : prev,
            );
        },
    });
};

export const useUpdateCampaign = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({ id, ...campaign }: Partial<Campaign> & { id: number }) =>
            (await axiosApiInstance.put(`/api/campaigns/items/${id}`, campaign)).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [KEY] });
        }
    });
};

export const useDeleteCampaign = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async (id: number) =>
            (await axiosApiInstance.delete(`/api/campaigns/items/${id}`)).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [KEY] });
        }
    });
};

export const useAppendCreators = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({ id, creatorIds }: { id: number; creatorIds: number[] }) =>
            (await axiosApiInstance.post(`/api/campaigns/items/${id}/creators`, { creator_ids: creatorIds })).data,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: [KEY] });
        }
    });
};

export const useUpdateCreatorMeta = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({
            campaignId,
            creatorId,
            ...body
        }: {
            campaignId: number;
            creatorId: number;
            budget?: number | null;
            comments?: string;
            approved?: boolean | null;
            pillar?: string;
            relationship_stage?: string;
            commission?: number | null;
            paid_amount?: number | null;
            paid_at?: string | null;
            payment_status?: string;
            contract_url?: string;
            contract_status?: string;
            po_number?: string;
        }) =>
            (await axiosApiInstance.patch(
                `/api/campaigns/items/${campaignId}/creators/${creatorId}/meta`,
                body,
            )).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
            qc.invalidateQueries({ queryKey: [KEY, 'roster', vars.campaignId] });
            qc.invalidateQueries({ queryKey: [KEY, 'payments', vars.campaignId] });
        },
    });
};

export const useAddCampaignDeliverable = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({
            campaignId,
            platform,
            post_type,
            quantity,
        }: { campaignId: number; platform: DeliverablePlatform; post_type: DeliverablePostType; quantity: number }) =>
            (await axiosApiInstance.post<CampaignDeliverable>(
                `/api/campaigns/items/${campaignId}/deliverables`,
                { platform, post_type, quantity },
            )).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
            qc.invalidateQueries({ queryKey: [KEY, 'roster', vars.campaignId] });
        },
    });
};

export const useDeleteCampaignDeliverable = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({ campaignId, deliverableId }: { campaignId: number; deliverableId: number }) =>
            (await axiosApiInstance.delete(
                `/api/campaigns/items/${campaignId}/deliverables/${deliverableId}`,
            )).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
            qc.invalidateQueries({ queryKey: [KEY, 'roster', vars.campaignId] });
        },
    });
};

export const useAddCreatorDeliverable = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({
            campaignId,
            creatorId,
            ...body
        }: { campaignId: number; creatorId: number } & Omit<CampaignCreatorDeliverable, 'id' | 'campaign_id' | 'creator_id' | 'completed'> & { completed?: number }) =>
            (await axiosApiInstance.post(
                `/api/campaigns/items/${campaignId}/creators/${creatorId}/deliverables`,
                body,
            )).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
            qc.invalidateQueries({ queryKey: [KEY, 'roster', vars.campaignId] });
        },
    });
};

export const useUpdateCreatorDeliverable = () => {
    // The page reads this client. jotai's queryClientAtom is a different instance,
    // so updates there never showed up until a full reload.
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({
            campaignId,
            creatorId,
            deliverableId,
            ...body
        }: { campaignId: number; creatorId: number; deliverableId: number } & Partial<CampaignCreatorDeliverable>) =>
            (await axiosApiInstance.patch(
                `/api/campaigns/items/${campaignId}/creators/${creatorId}/deliverables/${deliverableId}`,
                body,
            )).data,
        onMutate: async (vars) => {
            const key = [KEY, 'roster', vars.campaignId] as const;
            const prev = qc.getQueryData<CampaignRoster>(key);
            if (prev) {
                qc.setQueryData<CampaignRoster>(key, {
                    ...prev,
                    creator_deliverables: prev.creator_deliverables.map((d) =>
                        d.id === vars.deliverableId
                            ? {
                                ...d,
                                platform: vars.platform ?? d.platform,
                                post_type: vars.post_type ?? d.post_type,
                                quantity: vars.quantity ?? d.quantity,
                                completed: vars.completed ?? d.completed,
                                cost_per_post: vars.cost_per_post,
                                review_by: vars.review_by,
                                publish_by: vars.publish_by,
                                usage_rights: vars.usage_rights,
                                boosted_cost: vars.boosted_cost,
                                boosted_on: vars.boosted_on,
                                rate: vars.rate,
                            }
                            : d,
                    ),
                });
            }
            await qc.cancelQueries({ queryKey: key });
            return { prev };
        },
        onError: (_err, vars, ctx) => {
            const key = [KEY, 'roster', vars.campaignId] as const;
            const cur = qc.getQueryData<CampaignRoster>(key);
            const shown = cur?.creator_deliverables.find((d) => d.id === vars.deliverableId)?.completed;
            // A newer click already moved the count; don't snap it backwards.
            if (shown === vars.completed && ctx?.prev) qc.setQueryData(key, ctx.prev);
        },
        onSuccess: (_data, vars) => {
            // The roster count was already written in onMutate. Refetching here races
            // with a faster second click and paints the older number again.
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
        },
    });
};

export const useDeleteCreatorDeliverable = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({
            campaignId,
            creatorId,
            deliverableId,
        }: { campaignId: number; creatorId: number; deliverableId: number }) =>
            (await axiosApiInstance.delete(
                `/api/campaigns/items/${campaignId}/creators/${creatorId}/deliverables/${deliverableId}`,
            )).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
            qc.invalidateQueries({ queryKey: [KEY, 'roster', vars.campaignId] });
        },
    });
};

export const useRefreshCampaign = () => {
    // The campaign page reads the provider client. jotai's queryClientAtom is a
    // different instance, so invalidating it left this page on "Refreshing…".
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (campaignId: number) =>
            (await axiosApiInstance.post(`/api/campaigns/items/${campaignId}/refresh`)).data,
        onSuccess: (_d, campaignId) => {
            qc.setQueryData<Campaign>([KEY, 'detail', campaignId], (prev) =>
                prev ? { ...prev, refresh_status: 'running', refresh_error: '' } : prev,
            );
            qc.invalidateQueries({ queryKey: [KEY, 'detail', campaignId] });
            qc.invalidateQueries({ queryKey: [KEY, 'posts', campaignId] });
        },
    });
};

export const useIncludeCampaignPost = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ campaignId, postId }: { campaignId: number; postId: number }) =>
            (await axiosApiInstance.post(`/api/campaigns/items/${campaignId}/posts/${postId}/include`)).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'posts', vars.campaignId] });
        },
    });
};

export const useDeleteCampaignPost = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ campaignId, postId }: { campaignId: number; postId: number }) =>
            (await axiosApiInstance.delete(`/api/campaigns/items/${campaignId}/posts/${postId}`)).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'posts', vars.campaignId] });
        },
    });
};

export const useDeleteCampaignDrafts = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (campaignId: number) =>
            (await axiosApiInstance.delete<{ deleted: number }>(`/api/campaigns/items/${campaignId}/posts/drafts`)).data,
        onSuccess: (_data, campaignId) => {
            qc.invalidateQueries({ queryKey: [KEY, 'posts', campaignId] });
        },
    });
};

export const useExcludeCampaignPost = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ campaignId, postId }: { campaignId: number; postId: number }) =>
            (await axiosApiInstance.post(`/api/campaigns/items/${campaignId}/posts/${postId}/exclude`)).data,
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'posts', vars.campaignId] });
        },
    });
};

export const useCampaignPosts = (id: number | undefined, params: { status?: string; search?: string; sort?: string; in_campaign?: string; creator_id?: string }) =>
    useQuery<CampaignPost[]>({
        queryKey: [KEY, 'posts', id, params],
        enabled: !!id,
        queryFn: async () =>
            (await axiosApiInstance.get<CampaignPost[]>(`/api/campaigns/items/${id}/posts`, { params })).data,
    });

export const useCampaignPerformance = (id: number | undefined) =>
    useQuery<PerformanceResult>({
        queryKey: [KEY, 'performance', id],
        enabled: !!id,
        queryFn: async () =>
            (await axiosApiInstance.get<PerformanceResult>(`/api/campaigns/items/${id}/performance`)).data,
    });

export const useCampaignSummary = (id: number | undefined) =>
    useQuery<SummaryResult>({
        queryKey: [KEY, 'summary', id],
        enabled: !!id,
        queryFn: async () =>
            (await axiosApiInstance.get<SummaryResult>(`/api/campaigns/items/${id}/summary`)).data,
    });

export const useCampaignPayments = (id: number | undefined) =>
    useQuery<PaymentsResult>({
        queryKey: [KEY, 'payments', id],
        enabled: !!id,
        queryFn: async () =>
            (await axiosApiInstance.get<PaymentsResult>(`/api/campaigns/items/${id}/payments`)).data,
    });

export const useCampaignReports = (id: number | undefined) =>
    useQuery<CampaignReport[]>({
        queryKey: [KEY, 'reports', id],
        enabled: !!id,
        queryFn: async () =>
            (await axiosApiInstance.get<CampaignReport[]>(`/api/campaigns/items/${id}/reports`)).data,
    });

export const useCampaignReport = (campaignId: number | undefined, reportId: number | string | undefined) =>
    useQuery<CampaignReport>({
        queryKey: [KEY, 'report', campaignId, reportId],
        enabled: !!campaignId && !!reportId,
        queryFn: async () =>
            (await axiosApiInstance.get<CampaignReport>(`/api/campaigns/items/${campaignId}/reports/${reportId}`)).data,
    });

export const useActiveReport = (campaignId: number | undefined, reportId: number | string | undefined) => {
    const token = useContext(ReportTokenContext);
    const authed = useCampaignReport(token ? undefined : campaignId, token ? undefined : reportId);
    const shared = useQuery<CampaignReport>({
        queryKey: ['public-report', token, 'meta'],
        enabled: !!token,
        queryFn: async () =>
            (await axiosApiInstance.get<CampaignReport>(`/api/public/campaign-reports/${token}`)).data,
    });
    return token ? shared : authed;
};

export const useSetReportShare = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ campaignId, reportId, enabled }: { campaignId: number; reportId: number | string; enabled: boolean }) =>
            (await axiosApiInstance.patch<{ share_token: string | null }>(
                `/api/campaigns/items/${campaignId}/reports/${reportId}/share`,
                { enabled },
                { skipErrorNotification: true },
            )).data,
        onSuccess: (data, vars) => {
            qc.setQueryData<CampaignReport>([KEY, 'report', vars.campaignId, vars.reportId], (prev) =>
                prev ? { ...prev, share_token: data.share_token } : prev,
            );
        },
    });
};

export const useCreateReport = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({ campaignId, ...body }: { campaignId: number; name: string; start_date?: string | null; end_date?: string | null }) =>
            (await axiosApiInstance.post<CampaignReport>(`/api/campaigns/items/${campaignId}/reports`, body)).data,
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'reports', vars.campaignId] });
            qc.invalidateQueries({ queryKey: [KEY, 'detail', vars.campaignId] });
        },
    });
};

export const useDeleteReport = () => {
    const qc = useAtomValue(queryClientAtom);
    return useMutation({
        mutationFn: async ({ campaignId, reportId }: { campaignId: number; reportId: number }) =>
            (await axiosApiInstance.delete(`/api/campaigns/items/${campaignId}/reports/${reportId}`)).data,
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: [KEY, 'reports', vars.campaignId] });
        },
    });
};

function reportQuery<T>(path: string) {
    return (campaignId: number | undefined, reportId: number | string | undefined) => {
        const token = useContext(ReportTokenContext);
        return useQuery<T>({
            queryKey: token ? ['public-report', token, path] : [KEY, 'report', campaignId, reportId, path],
            enabled: token ? true : !!campaignId && !!reportId,
            queryFn: async () =>
                (await axiosApiInstance.get<T>(
                    token
                        ? `/api/public/campaign-reports/${token}/${path}`
                        : `/api/campaigns/items/${campaignId}/reports/${reportId}/${path}`,
                )).data,
        });
    };
}

export const useReportSummary = reportQuery<SummaryResult>('summary');
export const useReportLeaderboard = reportQuery<LeaderboardResult>('leaderboard');
export const useReportPerformance = reportQuery<PerformanceResult>('performance');
export const useReportTimeline = reportQuery<SummaryResult>('timeline');
export const useReportPlatforms = reportQuery<LeaderboardResult>('platforms');
export const useReportContentTypes = reportQuery<ContentTypeBreakdown>('content-types');
export const useReportHeatmap = reportQuery<HeatmapResult>('heatmap');
export const useReportTopPosts = reportQuery<CampaignPost[]>('top-posts');
export const useReportInfluencers = reportQuery<LeaderboardInfluencer[]>('influencers');

export type TaskPatch = {
    title?: string;
    done?: boolean;
    due_date?: string | null;
    clear_due?: boolean;
    assignee_id?: number | null;
    assignee_uid?: string | null;
    clear_assignee?: boolean;
    note?: string;
};

export type AssigneeOption = {
    kind: 'employee' | 'user';
    id?: number;
    uid?: string;
    name: string;
    email?: string;
};

export const useCreatorColumnPref = (uid: string | undefined) =>
    useQuery<{ columns: string[] | null }>({
        queryKey: [KEY, 'creator-columns', uid],
        enabled: !!uid,
        queryFn: async () =>
            (await axiosApiInstance.get<{ columns: string[] | null }>('/api/campaigns/prefs/creator-columns')).data,
        staleTime: 60 * 1000,
    });

export const useSaveCreatorColumns = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ uid, columns }: { uid: string; columns: string[] }) =>
            (await axiosApiInstance.put<{ columns: string[] }>(
                '/api/campaigns/prefs/creator-columns',
                { columns },
                { skipErrorNotification: true },
            )).data,
        onSuccess: (data, vars) => {
            qc.setQueryData([KEY, 'creator-columns', vars.uid], data);
        },
    });
};

export const useCampaignTasks = (id: number | undefined) =>
    useQuery<CampaignTask[]>({
        queryKey: [KEY, 'tasks', id],
        enabled: !!id,
        queryFn: async () =>
            (await axiosApiInstance.get<CampaignTask[]>(`/api/campaigns/items/${id}/tasks`)).data,
    });

export const useCreateCampaignTask = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ campaignId, title, assigneeId, assigneeUid, dueDate, note }: {
            campaignId: number; title: string; assigneeId?: number; assigneeUid?: string; dueDate?: string; note?: string;
        }) =>
            (await axiosApiInstance.post<CampaignTask>(`/api/campaigns/items/${campaignId}/tasks`, {
                title,
                assignee_id: assigneeId,
                assignee_uid: assigneeUid,
                due_date: dueDate,
                note,
            }, { skipErrorNotification: true })).data,
        onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: [KEY, 'tasks', vars.campaignId] }),
    });
};

export const useUpdateCampaignTask = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ campaignId, taskId, patch }: { campaignId: number; taskId: number; patch: TaskPatch }) =>
            (await axiosApiInstance.patch<CampaignTask>(
                `/api/campaigns/items/${campaignId}/tasks/${taskId}`,
                patch,
                { skipErrorNotification: true },
            )).data,
        onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: [KEY, 'tasks', vars.campaignId] }),
    });
};

export const useDeleteCampaignTask = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ campaignId, taskId }: { campaignId: number; taskId: number }) =>
            (await axiosApiInstance.delete(`/api/campaigns/items/${campaignId}/tasks/${taskId}`)).data,
        onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: [KEY, 'tasks', vars.campaignId] }),
    });
};
